import { useEffect, useState } from 'react'
import { UserView, UserViewData } from '@/features/UserView'
import { DeleteConfirmModal } from '@/features/DeleteConfirmModal'
import { BasePage } from '../BasePage'
import { useNavigate, useParams } from 'react-router-dom'
import { css } from '@emotion/react'
import { MainTitle } from '@/components/title/MainTitle'
import { BaseButton } from '@/components/button/BaseButton'
import { BackButton } from '@/components/button/BackButton'
import { DetailViewContainer } from '@/components/detail/DetailViewContainer'
import { useFetch } from '@/hooks/useFetch'
import { DELETE } from '@/api/api'
import { showSuccess, showError } from '@/shared/toast'

const DETAIL_PAGE_WIDTH = 918

const pageContainerStyle = css`
  display: flex;
  flex-direction: column;
`

const pageHeaderStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 918px;
  max-width: 100%;
  margin-bottom: 12px;
`

const headerLeftStyle = css`
  display: flex;
  align-items: center;
  gap: 16px;
`

const buttonAreaStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 918px;
  max-width: 100%;
  margin-top: 24px;
`

const leftButtonsStyle = css`
  display: flex;
  gap: 16px;
`

const buttonIconStyle = css`
  width: 16px;
  height: 16px;
  fill: currentColor;
`

// APIレスポンスの型
type UserApiResponse = {
  id: number
  loginId: string
  name: string
  role: number
  roleName: string
}

export const UserDetailPage = () => {
  const params = useParams()
  const navigate = useNavigate()
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  const { data: apiData, error } = useFetch<UserApiResponse>(
    `/api/users/${params.id}`,
  )

  // APIレスポンスをUserViewData形式に変換
  const data: UserViewData | null = apiData
    ? {
        id: apiData.id,
        loginId: apiData.loginId,
        name: apiData.name,
        roleName: apiData.roleName,
      }
    : null

  useEffect(() => {
    if (error) {
      showError('アカウント情報の取得に失敗しました。')
    }
  }, [error])

  const handleDeleteClick = () => {
    setIsDeleteModalOpen(true)
  }

  const handleDeleteConfirm = () => {
    DELETE(`/api/users/${params.id}`)
      .then(() => {
        showSuccess('アカウントを削除しました。')
        navigate('/users')
      })
      .catch(() => {
        showError('アカウントの削除に失敗しました。')
      })
    setIsDeleteModalOpen(false)
  }

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false)
  }

  const handleEditClick = () => {
    navigate(`/users/${params.id}/edit`)
  }

  if (!data) {
    return (
      <BasePage title="アカウント詳細">
        <div />
      </BasePage>
    )
  }

  return (
    <BasePage title="アカウント詳細">
      <div css={pageContainerStyle}>
        <div css={pageHeaderStyle}>
          <div css={headerLeftStyle}>
            <BackButton to="/users" />
            <MainTitle>アカウント詳細</MainTitle>
          </div>
        </div>
        <DetailViewContainer width={DETAIL_PAGE_WIDTH}>
          <UserView data={data} />
        </DetailViewContainer>
        <div css={buttonAreaStyle}>
          <div css={leftButtonsStyle}>
            <BaseButton
              size="lg"
              color="dangerOutline"
              onClick={handleDeleteClick}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 448 512"
                css={buttonIconStyle}
              >
                <path d="M135.2 17.7L128 32 32 32C14.3 32 0 46.3 0 64S14.3 96 32 96l384 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-96 0-7.2-14.3C307.4 6.8 296.3 0 284.2 0L163.8 0c-12.1 0-23.2 6.8-28.6 17.7zM416 128L32 128 53.2 467c1.6 25.3 22.6 45 47.9 45l245.8 0c25.3 0 46.3-19.7 47.9-45L416 128z" />
              </svg>
              削除
            </BaseButton>
          </div>
          <BaseButton size="lg" color="outline" onClick={handleEditClick}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 512 512"
              css={buttonIconStyle}
            >
              <path d="M471.6 21.7c-21.9-21.9-57.3-21.9-79.2 0L362.3 51.7l97.9 97.9 30.1-30.1c21.9-21.9 21.9-57.3 0-79.2L471.6 21.7zm-299.2 220c-6.1 6.1-10.8 13.6-13.5 21.9l-29.6 88.8c-2.9 8.6-.6 18.1 5.8 24.6s15.9 8.7 24.6 5.8l88.8-29.6c8.2-2.7 15.7-7.4 21.9-13.5L437.7 172.3 339.7 74.3 172.4 241.7zM96 64C43 64 0 107 0 160L0 416c0 53 43 96 96 96l256 0c53 0 96-43 96-96l0-96c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 96c0 17.7-14.3 32-32 32L96 448c-17.7 0-32-14.3-32-32l0-256c0-17.7 14.3-32 32-32l96 0c17.7 0 32-14.3 32-32s-14.3-32-32-32L96 64z" />
            </svg>
            編集
          </BaseButton>
        </div>
      </div>
      {isDeleteModalOpen && (
        <DeleteConfirmModal
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
        />
      )}
    </BasePage>
  )
}
